/**
 * Clear all order data after successful order submission
 * Equivalent to Flutter's clearAllDataAfterSuccessfulOrder
 */

import { AppDispatch } from '../app/store'
import { isCustomerServiceUser } from './apiUtils'

// Import all required actions
import { clearDiscount } from '../features/discount/discountSlice'
import { clearCartLocally, setOrderTypeLocally, setPaymentMethod, setNotes, clearDeliveryAddressLocally } from '../features/cart/cartSlice'
import { clearCartItemsImmediately, clearCartItemsFromApi } from '../features/cart/cartItemsSlice'
import { clearCartLocally as clearCsCartLocally } from '../features/csFlow/csCartSlice'
import { clearCustomerData } from '../features/customer/customerSlice'
import { clearSelectedTable } from '../features/tables/tablesSlice'
import { resetCsFlow } from '../features/csFlow/csFlowSlice'

/**
 * Clear all data after successful order submission
 * This function mirrors the Flutter implementation's clearAllDataAfterSuccessfulOrder
 * 
 * @param dispatch - Redux dispatch function
 */
export async function clearAllDataAfterSuccessfulOrder(dispatch: AppDispatch): Promise<void> {
  try {
    // 0. Clear discount/coupon immediately (no delay)
    try {
      dispatch(clearDiscount())
    } catch (e) {
      // Ignore errors
    }

    // 1. Clear cart - optimized to avoid multiple API calls
    // CRITICAL: Clear locally first, then from API, and ensure flag is set
    try {
      const isCS = isCustomerServiceUser()
      
      if (isCS) {
        // Clear CS cart - clear locally immediately to avoid delay
        dispatch(clearCsCartLocally())
        // Note: CS cart is cleared on the backend when order is created
        // No need to call API separately as the order creation already clears it
        // The wasClearedAfterOrder flag is set by clearCartItemsImmediately for CS too
      } else {
        // Clear regular cart - clear locally immediately to avoid delay
        // This sets wasClearedAfterOrder flag to prevent refetching
        dispatch(clearCartItemsImmediately())
        dispatch(clearCartLocally())
        
        // Clear cart from API to ensure backend is also cleared
        // This prevents data from being reloaded when returning to home page
        // The flag is already set by clearCartItemsImmediately, so even if API fails,
        // the flag will prevent refetching
        try {
          await dispatch(clearCartItemsFromApi() as any)
        } catch (apiError) {
          // If API clear fails, continue anyway - local state is already cleared
          // and wasClearedAfterOrder flag is set, so refetching is prevented
        }
      }
    } catch (e) {
      // If failed, try to clear locally only
      // This will still set the wasClearedAfterOrder flag
      try {
        const isCS = isCustomerServiceUser()
        if (isCS) {
          dispatch(clearCsCartLocally())
        } else {
          dispatch(clearCartItemsImmediately())
          dispatch(clearCartLocally())
        }
      } catch (e) {
        // Ignore errors
      }
    }

    // 2. Clear customer data
    try {
      dispatch(clearCustomerData())
    } catch (e) {
      // Ignore errors
    }

    // 3. Clear notes
    try {
      dispatch(setNotes(''))
    } catch (e) {
      // Ignore errors
    }

    // 4. Clear selected address
    try {
      dispatch(clearDeliveryAddressLocally())
      localStorage.removeItem('selected_address_id')
    } catch (e) {
      // Ignore errors
    }

    // 5. Clear discount/coupon (again, as in Flutter)
    try {
      dispatch(clearDiscount())
    } catch (e) {
      // Ignore errors
    }

    // 6. Clear selected table
    try {
      await dispatch(clearSelectedTable() as any)
    } catch (e) {
      // Ignore errors
    }

    // 7. Reset order type to default (takeaway)
    try {
      dispatch(setOrderTypeLocally('takeaway'))
    } catch (e) {
      // Ignore errors
    }

    // 8. Reset payment method to cash
    try {
      dispatch(setPaymentMethod('cash'))
    } catch (e) {
      // Ignore errors
    }

    // 9. Clear order details (if exists)
    // Note: OrderDetailsController clearing logic can be added here if needed
    try {
      // OrderDetailsController may not exist in all cases
      // Ignore this error
    } catch (e) {
      // Ignore errors
    }

    // 10. Clear CS Flow data for CS users
    try {
      const isCS = isCustomerServiceUser()
      if (isCS) {
        dispatch(resetCsFlow())
      }
    } catch (e) {
      // Ignore errors
    }
  } catch (e) {
    // Ignore all errors - ensure UI is cleared even if some operations fail
  }
}

